import React, { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  Alert,
  SafeAreaView,
  Platform,
} from "react-native";
import { useTranslation } from "../../../../i18n/useTranslation";
import { consumerLookup } from "../../../../services/send.service";
import {
  applyFirstSenderLookupInfoToSession,
  isSenderLookupOnlyConsumerResponse,
} from "../../../../helper/senderLookupProfileHelper";
import { SessionState, useSessionStore } from "../../../../../../state/src/useSessionStore";
import  FloatingTextInputComponent from "../../common/FloatingTextInputComponent";
import Colors from "../../../../constants/colors";
import { ComponentStyles } from "../../../../constants/componentStyles";
import { capturePosthogEvent } from "../../../../utils/analytics";

type Props = {
  onValidationChange?: (valid: boolean) => void;
  testID?: string | undefined;
  /** When false, the field does not focus on mount (e.g. Receive flow after earlier steps). Default true. */
  isAutoFocus?: boolean;
};

export type PhoneLookupSubmitResult = 'ok' | 'notfound' | 'externalSenderProfile';

export type EnterPhoneStepHandle = {
  submit: () => Promise<PhoneLookupSubmitResult>;
  phoneSubmit: () => Promise<boolean>;
};

const EnterPhoneStep = forwardRef<EnterPhoneStepHandle, Props>(
  ({ onValidationChange, testID, isAutoFocus = true }: Props, ref) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({
      phoneNumber: "",
    });
  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
  const setPhoneNumberStore = useSessionStore((s: SessionState) => (s as any).setPhoneNumber);
  
  const setIsNewConsumer = useSessionStore((s: SessionState) => (s as any).setIsNewConsumer);
  const storedPhoneNumber = useSessionStore((s: SessionState) => (s as any).phoneNumber) || "";
  console.log('setPhoneNumberStore', storedPhoneNumber)
  const [phoneNumber, setPhoneNumber] = useState(
    () => String((useSessionStore.getState() as any).phoneNumber ?? "").replace(/\D/g, "")
  );

  useEffect(() => {
      setPhoneNumber(storedPhoneNumber || "");
    }, [storedPhoneNumber]);

  const formatPhoneNumber = (number: string) => {
    const cleaned = number.replace(/\D/g, "");
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const submit = async (): Promise<PhoneLookupSubmitResult> => {
    if (phoneNumber.length !== 10) {
      Alert.alert("Error", t("PHONE_STEP_ERROR_INVALID"));
      throw new Error('Invalid phone number');
    }
    if (!agentId || !unitProfileId) {
      Alert.alert("Error", t("PHONE_STEP_ERROR_MISSING_LOCATION"));
      throw new Error('Missing agent or unit profile');
    }
    try {
      const ConsumerLookupInfo = await consumerLookup(phoneNumber, agentId, unitProfileId);
      console.log("consumerLookup Response:", ConsumerLookupInfo);
      if (isSenderLookupOnlyConsumerResponse(ConsumerLookupInfo)) {
        applyFirstSenderLookupInfoToSession(ConsumerLookupInfo);
        try { setIsNewConsumer?.(true); } catch {}
        capturePhoneEvent('Send Flow - Phone Capture Step', phoneNumber);
        return 'externalSenderProfile';
      }
      try { setIsNewConsumer?.(false); } catch {}
      capturePhoneEvent('Send Flow - Phone Capture Step',phoneNumber);
      return 'ok';
    } catch (error: any) {
      const code = error?.code || error?.ErrorDetail?.ErrorCode;
      if (String(code) === "616") {
        try { setIsNewConsumer?.(true); } catch {}
        capturePhoneEvent('Send Flow - Phone Capture Step',phoneNumber);
        return 'notfound';
      }
      try { setIsNewConsumer?.(false); } catch {}
      Alert.alert("Error", t("PHONE_STEP_ERROR_FAILED"));
      throw error;
    } finally {
      console.log('');
    }
  };

  const capturePhoneEvent = (event: string, phoneNumber: string) => {
    capturePosthogEvent(event, {
      'data-mg-phone-number': phoneNumber
    });
  }

  useImperativeHandle(ref, () => ({
    submit,
    phoneSubmit: async () => {
      const newErrors = {
        phoneNumber: phoneNumber.trim() ? "" : "Invalid phone number",
      };
      setErrors(newErrors);
  
      // if any error string is non-empty
      const isValid = !Object.values(newErrors).some(Boolean);
      if(isValid)
        capturePhoneEvent('Receive Flow - Phone Capture Step', phoneNumber);
      return isValid;
    },
  }));

  const handlePhoneChange = (text: string) => {
    if (errors.phoneNumber) setErrors({ ...errors, phoneNumber: "" });
    const cleaned = text.replace(/\D/g, "");
    if (cleaned?.length <= 10) {
      setPhoneNumber(cleaned);
      try { setPhoneNumberStore?.(cleaned.length === 10 ? cleaned : null); } catch {}
      // Tell parent if valid
      onValidationChange?.(cleaned?.length === 10);
    }
  };

  const Wrapper = Platform.OS === "web" ? View : SafeAreaView;
  const marginBottomSpace = errors?.phoneNumber?.length > 0 ? 24 : 0
  return (
    <Wrapper style={styles.container} testID={testID}>
      <Text style={styles.title}>{t("PHONE_STEP_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("PHONE_STEP_SUBTITLE")}</Text>
      <FloatingTextInputComponent
        testID="phone-input"
        label={t("PHONE_STEP_PLACEHOLDER")}
        value={formatPhoneNumber(phoneNumber)}
        onChangeText={handlePhoneChange}
        keyboardType="number-pad"
        errorText={errors.phoneNumber}
        onClearError={() => setErrors((prev) => ({ ...prev, phoneNumber: "" }))}
        isAutoFocus={isAutoFocus}
      />
    </Wrapper>
  );
});

export default EnterPhoneStep;

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  scrollContainer: {
    paddingHorizontal: 24,
    paddingBottom: 40,
    alignItems: "stretch",
    justifyContent: "flex-start"
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24
  },
  phoneInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 20,
  },
  countryCodeSelector: {
    height: 56,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: Colors.textInputBackground,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 6,
    marginRight: 8,
  },
  countryText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#111827",
    marginRight: 4,
  },
  plusText: {
    fontSize: 16,
    fontFamily: 'Rubik',
    fontWeight: "400",
    lineHeight: 24,
    color: Colors.titleTextColor,
    marginLeft: 8
  },
  textInput: {
    width: 295,
    height: 56,
    backgroundColor: Colors.textInputBackground,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: Platform.OS === "ios" ? 12 : 6,
    fontSize: 16,
    fontWeight: "400",
    lineHeight: 24,
    color: "#111827",
  },
  flagContainer: {
    height: 24,
    width: 24,
    borderRadius: 50,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
  },
});